/** UI definitions only. Amounts, percentages and their availability come from the server. */
export const SUMMARY_METRIC_INFO_IDS = [
  "totalValue", "invested", "unrealizedPnL", "partialValue", "partialUnrealizedPnL",
  "realizedPnL", "dividends", "cash", "commissions", "taxes", "deposits", "withdrawals",
  "totalReturn", "totalReturnNet", "xirr", "dividendYield",
  "assetValue", "assetCostBasis", "assetUnrealizedPnL",
] as const;
export type SummaryMetricInfoId = (typeof SUMMARY_METRIC_INFO_IDS)[number];

export interface SummaryMetricDefinition {
  label: string;
  paragraphs: readonly string[];
}

export const SUMMARY_METRIC_INFO: Record<SummaryMetricInfoId, SummaryMetricDefinition> = {
  totalValue: { label: "Total Value", paragraphs: [
    "Стоимость позиций и денежные остатки выбранного портфеля на выбранную дату, в валюте отображения.",
    "Если не хватает нужной оценки или сведений об остатках, полный итог недоступен. Известная часть показана отдельно.",
  ] },
  invested: { label: "Invested", paragraphs: [
    "Оставшаяся себестоимость позиций с относимыми комиссиями покупки на выбранную дату.",
    "Это не сумма всех когда-либо внесённых денег. Неизвестная себестоимость не считается нулевой.",
  ] },
  unrealizedPnL: { label: "Unreal. P&L", paragraphs: [
    "Текущая стоимость открытых позиций минус их оставшаяся себестоимость на выбранную дату.",
    "Этот результат ещё не зафиксирован закрытием. Полная сумма требует известных стоимости и себестоимости.",
  ] },
  partialValue: { label: "Partial Value", paragraphs: [
    "Известная оценённая часть капитала выбранного портфеля на выбранную дату при неполных данных.",
    "Это не полный итог: пропущенные позиции и остатки не равны нулю.",
  ] },
  partialUnrealizedPnL: { label: "Partial Unreal. P&L", paragraphs: [
    "Известный нереализованный результат только тех открытых позиций, для которых хватает данных на выбранную дату.",
    "Это не полный результат портфеля. Недоступная часть не считается нулевой.",
  ] },
  realizedPnL: { label: "Real. P&L", paragraphs: [
    "Прибыль или убыток закрытых частей позиций до выбранной даты с торговыми комиссиями.",
    "Продажи сопоставляются с покупками по FIFO: сначала закрываются самые ранние покупки. Сумма включает торговый и валютный результат.",
  ] },
  dividends: { label: "Dividends", paragraphs: [
    "Подтверждённые начисления дивидендов до налога до выбранной даты.",
    "При неизвестной сумме до налога полный итог недоступен; известное чистое поступление подписано отдельно. Учтённый налог показывается отдельно и повторно не списывается.",
  ] },
  cash: { label: "Cash", paragraphs: [
    "Денежные остатки выбранных счетов в разных валютах на выбранную дату, переведённые в валюту отображения.",
    "Отрицательный остаток означает долг. Ниже сохранена разбивка по счетам и исходным валютам, когда она доступна.",
  ] },
  commissions: { label: "Commissions", paragraphs: [
    "Учтённые торговые и отдельные комиссии за вычетом возвратов до выбранной даты. Отрицательный итог означает, что возвраты превысили расходы.",
    "Они уже отражены в результате по действующим правилам. Повторно вычитать эту сумму из результата не нужно.",
  ] },
  taxes: { label: "Taxes", paragraphs: [
    "Учтённые удержанные налоги за вычетом возвратов до выбранной даты.",
    "Это совершённые денежные операции, а не расчёт будущего налога. При неполных налоговых сведениях полный итог недоступен.",
  ] },
  deposits: { label: "Deposits", paragraphs: [
    "Учтённые внешние пополнения выбранного портфеля до выбранной даты.",
    "Операции, распознанные как внутренние переводы между счетами, к пополнениям не относятся.",
  ] },
  withdrawals: { label: "Withdrawals", paragraphs: [
    "Учтённые внешние выводы денег из выбранного портфеля до выбранной даты.",
    "Операции, распознанные как внутренние переводы между счетами, к выводам не относятся.",
  ] },
  totalReturn: { label: "Total Return", paragraphs: [
    "Общий денежный результат до налогов на выбранную дату: закрытые и открытые позиции, начисленные дивиденды, денежная переоценка и комиссии.",
    "При неизвестных начислениях до налога зависимый полный результат недоступен. Комиссии уже учтены и второй раз не вычитаются.",
  ] },
  totalReturnNet: { label: "Total Return Net", paragraphs: [
    "Общий денежный результат после налогов на выбранную дату с учётом подтверждённых чистых поступлений.",
    "Он может быть известен при неизвестной сумме до налога или неизвестном налоге. Удержание, уже включённое в чистую выплату, повторно не вычитается.",
  ] },
  xirr: { label: "XIRR", paragraphs: [
    "Годовой темп доходности денег с учётом сумм и дат внешних пополнений и выводов и стоимости портфеля на выбранную дату.",
    "Расчёт ведётся в USD. Переключение валюты отображения его не меняет. Это не накопленный процент за всю историю и не среднее годовых процентов. Внутренние сделки и переводы не считаются внешними вложениями.",
    "Нужны полная рыночная стоимость и подтверждённые внешние денежные потоки. Неизвестные налог или себестоимость сами по себе не блокируют ставку. Если необходимых данных или рассчитываемой ставки нет, показатель недоступен. Отдельный XIRR для опционов не рассчитывается.",
    "Условный пример: вложение 1000 USD и стоимость 1104,70 USD через 365 дней без других внешних движений соответствуют 10,47% годовых. Это иллюстрация, а не проверка значения вашего портфеля.",
  ] },
  dividendYield: { label: "Dividend Yield", paragraphs: [
    "Подтверждённые накопленные дивиденды до налога относительно известной оставшейся себестоимости позиций на выбранную дату.",
    "При неизвестном числителе или базе полный процент недоступен. Это накопленное отношение, а не прогноз годовой дивидендной доходности.",
  ] },
  assetValue: { label: "Стоимость класса", paragraphs: [
    "Сумма оценок всех открытых позиций этого типа активов в выбранном составе и на выбранную дату. Для денег показан остаток, в том числе долг.",
    "Используется серверная сводка всего выбранного состава: страницы таблицы не ограничивают эту сумму. Неполная оценка и её известная часть подписаны отдельно.",
  ] },
  assetCostBasis: { label: "Оставшаяся себестоимость класса", paragraphs: [
    "Сумма оставшейся себестоимости открытых позиций этого типа активов с относимыми комиссиями покупки на выбранную дату.",
    "Для денег себестоимость позиции неприменима. Неизвестная база не заменяется нулём.",
  ] },
  assetUnrealizedPnL: { label: "Нереализованный результат класса", paragraphs: [
    "Сумма нереализованного результата всех открытых позиций этого типа активов на выбранную дату.",
    "Процент — эта сумма, делённая на оставшуюся себестоимость класса, а не среднее процентов отдельных контрактов. При нулевой или неизвестной базе процент недоступен. Для денег результат позиции неприменим; денежная переоценка остаётся в общем результате портфеля.",
  ] },
};

export function summaryMetricInfoIdForLabel(label: string): SummaryMetricInfoId | undefined {
  return SUMMARY_METRIC_INFO_IDS.find((id) => SUMMARY_METRIC_INFO[id].label === label);
}
